import { useMemo, useCallback } from "react"
import {
  Avatar,
  Box,
  Card,
  CardMedia,
  Divider,
  Stack,
  Typography,
  useTheme,
  Chip,
  SxProps,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import { useNavigate } from "react-router-dom"
import get from "lodash/get"

import { getIcon } from "utils/icon"
import {
  getCategoryIcon,
  getFeatureDetail,
  IOccupantDetail,
} from "utils/feature"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"

import { StoreIcon } from "components/icons"
import { ImdfFeature } from "providers/venue/types"
import { ComingSoonChip, NewShopChip } from "components/chips"
export interface IDirectoryOccupantCardProps {
  data: ImdfFeature
  sx?: SxProps
  endAdornmentSlotClasses?: { [k: string]: any }
  endAdornmentSlots?: { [k: string]: any }
  isNewShop?: boolean
  isComingSoon?: boolean
}

export const DirectoryOccupantCard: React.FC<IDirectoryOccupantCardProps> = ({
  data,
  sx,
  endAdornmentSlotClasses,
  endAdornmentSlots,
  isNewShop = false,
  isComingSoon = false,
  ...props
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMultiVenueProject } = useVenue()
  const categoryIcon = useMemo(() => {
    const parentCategory = get(data, "properties.local_categories[0]", {})
    return getCategoryIcon(parentCategory)
  }, [data])
  const { name, logo, group, category, level, venueLogo, boothName, isAd } =
    useMemo(() => getFeatureDetail(data) as IOccupantDetail, [data])

  const showVenueLogo =
    isMultiVenueProject && venueLogo && !data.properties?.hasMultipleLocations

  const handleClick = useCallback(
    () => navigate(`/maps/place/${data.id}`),
    [navigate, data?.id]
  )

  const theme = useTheme()
  const { styleOverrides = {} } =
    theme.components?.[`DirectoryOccupantCard`] || {}
  const {
    root,
    occupantLogo,
    cardContent,
    levelContainer,
    divider,
    venueLogo: venueLogoStyle,
    titleText,
    groupText,
    categoryText,
    titleContainer,
    detailWrapper,
    levelContainerWrapper,
    locationInfoContainer,
    boothText,
    infoContainer,
    chip,
    newShopChip,
    comingSoonChip,
    titleWrapper,
  } = styleOverrides

  return (
    // TODO:: Render collapse item when hasMultipleLocations (has same name.en)
    <Card sx={deepmerge(root, sx)} onClick={handleClick} {...props}>
      <Box sx={occupantLogo}>
        <Avatar src={logo}>
          {getIcon(categoryIcon, null, {
            imageIconProps: {
              solidColorFilterId: "directoryCard_avatar_filter",
            },
          }) || <StoreIcon />}
        </Avatar>
      </Box>
      <Box sx={cardContent}>
        <Box sx={titleContainer}>
          <Box sx={titleWrapper}>
            {isNewShop && !isAd && <NewShopChip sx={newShopChip} />}
            {isComingSoon && !isAd && <ComingSoonChip sx={comingSoonChip} />}
            <Typography sx={titleText}>{t(name)}</Typography>
            {isAd && <Chip label={t("sponsored.search_result")} sx={chip} />}
          </Box>
          {boothName && (
            <Typography sx={boothText}>
              {`${t("Booth")} ${t(boothName)}`}
            </Typography>
          )}
        </Box>
        <Stack spacing={1} sx={detailWrapper}>
          <Divider sx={divider} />
          <Stack
            direction="row"
            sx={infoContainer}
            justifyContent="space-between"
          >
            <Stack justifyContent="end" spacing={1} sx={locationInfoContainer}>
              {showVenueLogo && (
                <CardMedia
                  component="img"
                  image={!data.properties?.hasMultipleLocations && venueLogo}
                  sx={venueLogoStyle}
                />
              )}
              <Typography variant="body2" color="grey" sx={groupText}>
                {t(group)}
              </Typography>
              <Typography variant="body2" sx={categoryText}>
                {t(category)}
              </Typography>
            </Stack>
            <Box sx={levelContainerWrapper}>
              <Box sx={levelContainer}>
                <Typography variant="h6">
                  {t(
                    data.properties?.hasMultipleLocations
                      ? "Multiple Locations"
                      : level
                  )}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </Card>
  )
}
